<template>
  <v-container fluid class="pa-0">
    <v-card class="mb-4 pa-4" outlined>
      <v-card-title class="pa-0 text-h6">Занятость</v-card-title>
    </v-card>

    <v-card class="mb-4" outlined>
      <v-card-text>
        <v-form ref="filterForm">
          <v-row dense>
            <v-col cols="12" md="4">
              <MonthSelector v-model="filters.month" />
            </v-col>
            <v-col cols="12" md="3">
              <ProjectTagSelector v-model="filters.projectIds" field-label="Проекты" no-rules />
            </v-col>
            <v-col cols="12" md="3">
              <ManagerSelector v-model="filters.userIds" field-label="Менеджеры" no-rules />
            </v-col>
            <v-col cols="12" md="2" class="d-flex align-center" style="gap: 8px">
              <v-btn color="primary" :loading="loading" @click="loadReport">Показать</v-btn>
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    icon
                    color="success"
                    :loading="exporting"
                    v-on="on"
                    @click="onExport"
                  >
                    <v-icon>mdi-file-excel</v-icon>
                  </v-btn>
                </template>
                <span>Выгрузить в Excel</span>
              </v-tooltip>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <v-card class="mb-4" outlined>
      <v-card-title class="text-subtitle-1">
        Занятость
        <v-spacer />
        <span class="caption grey--text">Итого: {{ totalDuration }}</span>
      </v-card-title>
      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="items"
          item-key="key"
          :loading="loading"
          loading-text="Загрузка…"
          no-data-text="Записи не найдены"
          :items-per-page="25"
          :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
          dense
        >
          <template #item.date="{ item }">
            <template v-if="item.is_period">
              {{ formatDate(item.date) }} — {{ formatDate(item.date_to) }}
              <div class="caption grey--text">за весь период</div>
            </template>
            <template v-else>{{ formatDate(item.date) }}</template>
          </template>
          <template #item.duration="{ item }">
            {{ item.duration }}
            <div v-if="item.is_period && item.minutes_in_range !== item.minutes" class="caption grey--text">
              в выбранных датах: {{ item.duration_in_range }}
            </div>
          </template>
          <template #item.actions="{ item }">
            <div class="d-flex justify-center" style="gap: 2px">
              <v-tooltip v-if="canEdit && item.can_edit" bottom>
                <template #activator="{ on, attrs }">
                  <v-btn v-bind="attrs" x-small color="info" icon v-on="on" @click="onEdit(item)">
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <span>{{ item.period ? 'Редактировать период' : 'Редактировать' }}</span>
              </v-tooltip>
              <v-tooltip v-if="canDelete && item.can_edit" bottom>
                <template #activator="{ on, attrs }">
                  <v-btn v-bind="attrs" x-small color="error" icon v-on="on" @click="onDelete(item)">
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </template>
                <span>{{ item.period ? 'Удалить период' : 'Удалить' }}</span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-card outlined>
      <v-card-title class="text-subtitle-1">Сводная за период</v-card-title>
      <v-card-text>
        <v-data-table
          :headers="summaryHeaders"
          :items="summary"
          :loading="loading"
          loading-text="Загрузка…"
          no-data-text="Записи не найдены"
          :items-per-page="-1"
          hide-default-footer
          dense
        />
      </v-card-text>
    </v-card>

    <div v-if="canCreate" class="document-flow-workloads-fab">
      <v-tooltip left>
        <template #activator="{ on, attrs }">
          <v-btn v-bind="attrs" color="success" dark fab v-on="on" @click="onCreate">
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>Добавить занятость</span>
      </v-tooltip>
    </div>

    <v-dialog v-model="formDialog" max-width="560" persistent>
      <v-card>
        <v-card-title>{{ formTitle }}</v-card-title>
        <v-card-text>
          <v-form ref="workloadForm" v-model="formValid">
            <ProjectTagSelector v-model="form.project_id" single />

            <PeriodRangeSelector v-model="form.period" field-label="Даты" :single-month="isPeriodMode" />

            <v-checkbox
              v-if="!editedPeriodId && periodDays > 1"
              v-model="form.whole_period"
              label="За весь период"
              :hint="form.whole_period ? 'Время указывается суммарно за весь период' : 'Время указывается за каждый день периода'"
              persistent-hint
              class="mt-0 mb-2"
            />


            <v-row dense>
              <v-col cols="6">
                <v-text-field
                  v-if="isPeriodMode"
                  v-model.number="form.hours"
                  type="number"
                  min="0"
                  label="Часы за период"
                  :rules="durationRule"
                />
                <v-select
                  v-else
                  v-model="form.hours"
                  :items="hoursOptions"
                  label="Часы"
                  :rules="durationRule"
                />
              </v-col>
              <v-col cols="6">
                <v-select
                  v-model="form.minutes"
                  :items="minutesOptions"
                  label="Минуты"
                  :rules="durationRule"
                />
              </v-col>
            </v-row>

            <div v-if="isPeriodMode && perDayDuration" class="caption grey--text mb-2">
              ≈ {{ perDayDuration }} в день, дней: {{ periodDays }}
            </div>

            <v-textarea
              v-model="form.description"
              label="Описание"
              rows="3"
              auto-grow
              counter="2000"
              :rules="[v => !!(v && v.trim()) || 'Данное поле обязательно']"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="saving" @click="closeForm">Отмена</v-btn>
          <v-btn color="primary" :loading="saving" @click="save">Сохранить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="420">
      <v-card>
        <v-card-title>Удалить занятость?</v-card-title>
        <v-card-text v-if="workloadToDelete && workloadToDelete.period">
          Период {{ formatDate(workloadToDelete.period.date_from) }}–{{ formatDate(workloadToDelete.period.date_to) }}
          будет удалён целиком со всеми днями.
        </v-card-text>
        <v-card-text v-else>
          Запись от {{ formatDate(workloadToDelete && workloadToDelete.date) }} будет удалена.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="deleting" @click="deleteDialog = false">Отмена</v-btn>
          <v-btn color="error" :loading="deleting" @click="confirmDelete">Удалить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import MonthSelector from '~/components/partials/selectors/MonthSelector.vue'
import PeriodRangeSelector from '~/components/partials/selectors/PeriodRangeSelector.vue'
import ProjectTagSelector from '~/components/partials/selectors/ProjectTagSelector.vue'
import ManagerSelector from '~/components/partials/selectors/ManagerSelector.vue'

export default {
  name: 'DocumentFlowWorkloadsList',

  components: { MonthSelector, PeriodRangeSelector, ProjectTagSelector, ManagerSelector },

  data() {
    return {
      filters: {
        month: null,
        projectIds: [],
        userIds: [],
      },
      items: [],
      summary: [],
      totalMinutes: 0,
      loading: false,
      exporting: false,
      loadTimer: null,
      requestId: 0,

      formDialog: false,
      formValid: false,
      saving: false,
      editedId: null,
      editedPeriodId: null,
      form: this.emptyForm(),

      deleteDialog: false,
      workloadToDelete: null,
      deleting: false,

      headers: [
        { text: 'Менеджер', value: 'user_name' },
        { text: 'Дата', value: 'date' },
        { text: 'Проект', value: 'project_name' },
        { text: 'Занятость', value: 'duration', align: 'end' },
        { text: 'Описание', value: 'description', sortable: false },
        { text: 'Действия', value: 'actions', align: 'center', sortable: false },
      ],
      summaryHeaders: [
        { text: 'Менеджер', value: 'user_name' },
        { text: 'Проект', value: 'project_name' },
        { text: 'Занятость', value: 'duration', align: 'end' },
      ],
    }
  },

  computed: {
    ...mapGetters({
      hasPermission: 'user/hasPermission',
    }),

    canCreate() {
      return this.hasPermission('statiz.document-flow.workloads.create')
    },

    canEdit() {
      return this.hasPermission('statiz.document-flow.workloads.edit')
    },

    canDelete() {
      return this.hasPermission('statiz.document-flow.workloads.delete')
    },

    hoursOptions() {
      return Array.from({ length: 25 }, (_, i) => i)
    },

    minutesOptions() {
      return Array.from({ length: 12 }, (_, i) => i * 5)
    },

    formTitle() {
      if (this.editedPeriodId) return 'Редактирование периода'
      return this.editedId ? 'Редактирование занятости' : 'Новая занятость'
    },

    periodDays() {
      return this.expandPeriod(this.form.period).length
    },

    isPeriodMode() {
      return !!this.editedPeriodId || (this.form.whole_period && this.periodDays > 1)
    },

    formMinutes() {
      return (Number(this.form.hours) || 0) * 60 + (Number(this.form.minutes) || 0)
    },

    perDayDuration() {
      if (!this.periodDays || !this.formMinutes) return ''
      const perDay = Math.round(this.formMinutes / this.periodDays)
      return `${Math.floor(perDay / 60)}:${String(perDay % 60).padStart(2, '0')}`
    },

    durationRule() {
      if (this.isPeriodMode) {
        return [
          () => this.formMinutes >= this.periodDays || 'Укажите занятость',
          () => this.formMinutes <= this.periodDays * 1440 || 'Не больше 24 часов в день',
        ]
      }

      return [() => this.formMinutes > 0 || 'Укажите занятость']
    },

    totalDuration() {
      return `${Math.floor(this.totalMinutes / 60)}:${String(this.totalMinutes % 60).padStart(2, '0')}`
    },

    monthBounds() {
      if (!this.filters.month) return [null, null]
      const [year, month] = this.filters.month.split('-').map(Number)
      return [this.toDateString(new Date(year, month - 1, 1)), this.toDateString(new Date(year, month, 0))]
    },

    requestParams() {
      return {
        date_from: this.monthBounds[0],
        date_to: this.monthBounds[1],
        project_ids: this.filters.projectIds,
        user_ids: this.filters.userIds,
      }
    },
  },

  watch: {
    isPeriodMode() {
      this.$nextTick(() => this.$refs.workloadForm && this.$refs.workloadForm.validate())
    },

    filters: {
      deep: true,
      handler() {
        this.syncQuery()
        this.scheduleLoad()
      },
    },
  },

  mounted() {
    this.applyQuery()
  },

  beforeDestroy() {
    clearTimeout(this.loadTimer)
  },

  methods: {
    ...mapActions('document_flow/workloads', [
      'fetchWorkloads',
      'exportWorkloads',
      'createWorkload',
      'updateWorkload',
      'deleteWorkload',
      'createWorkloadPeriod',
      'updateWorkloadPeriod',
      'deleteWorkloadPeriod',
    ]),

    emptyForm() {
      return {
        project_id: null,
        period: [],
        whole_period: false,
        hours: 0,
        minutes: 0,
        description: '',
      }
    },

    applyQuery() {
      const { month, project_ids: projectIds, user_ids: userIds } = this.$route.query

      this.filters.month = /^\d{4}-(0[1-9]|1[0-2])$/.test(month || '')
        ? month
        : this.toDateString(new Date()).slice(0, 7)

      this.filters.projectIds = this.parseIds(projectIds)
      this.filters.userIds = this.parseIds(userIds)
    },

    parseIds(value) {
      if (!value) return []
      return String(value)
        .split(',')
        .map(Number)
        .filter(id => Number.isInteger(id) && id > 0)
    },

    syncQuery() {
      const query = {}

      if (this.filters.month) query.month = this.filters.month
      if (this.filters.projectIds.length) query.project_ids = this.filters.projectIds.join(',')
      if (this.filters.userIds.length) query.user_ids = this.filters.userIds.join(',')

      const current = this.$route.query
      const keys = new Set([...Object.keys(query), ...Object.keys(current)])
      if ([...keys].every(key => String(query[key] ?? '') === String(current[key] ?? ''))) return

      this.$router.replace({ query }).catch(() => {})
    },

    expandPeriod(period) {
      if (period.length !== 2 || period.includes(null)) return []

      const dates = []
      const last = new Date(period[1])

      for (let day = new Date(period[0]); day <= last; day.setDate(day.getDate() + 1)) {
        dates.push(this.toDateString(day))
      }

      return dates
    },

    toDateString(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    formatDate(value) {
      if (!value) return '—'
      const [year, month, day] = String(value).split('-')
      return `${day}.${month}.${year}`
    },

    async showError(error, fallback) {
      let data = error?.response?.data

      if (data instanceof Blob) {
        try {
          data = JSON.parse(await data.text())
        } catch (e) {
          data = null
        }
      }

      const details = Object.values(data?.errors || {}).flat()

      this.$store.commit('shared/SET_ERROR', { message: data?.message || fallback, details }, { root: true })
    },

    scheduleLoad() {
      clearTimeout(this.loadTimer)

      if (!this.filters.month) return

      this.loadTimer = setTimeout(() => this.loadReport(), 400)
    },

    async loadReport() {
      clearTimeout(this.loadTimer)
      if (!this.$refs.filterForm.validate()) return

      const requestId = ++this.requestId
      this.loading = true
      try {
        const response = await this.fetchWorkloads(this.requestParams)
        if (requestId !== this.requestId) return
        this.items = response.items || []
        this.summary = response.summary || []
        this.totalMinutes = response.total_minutes || 0
      } catch (error) {
        if (requestId !== this.requestId) return
        this.items = []
        this.summary = []
        this.totalMinutes = 0
        await this.showError(error, 'Не удалось загрузить занятость')
        console.error(error)
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },

    async onExport() {
      if (!this.$refs.filterForm.validate()) return

      this.exporting = true
      try {
        const response = await this.exportWorkloads(this.requestParams)
        this.utils.downloadXlsx(response)
      } catch (error) {
        await this.showError(error, 'Не удалось выгрузить отчёт')
        console.error(error)
      } finally {
        this.exporting = false
      }
    },

    onCreate() {
      this.editedId = null
      this.editedPeriodId = null
      this.form = this.emptyForm()
      this.form.period = [this.toDateString(new Date()), this.toDateString(new Date())]
      this.formDialog = true
      this.$nextTick(() => this.$refs.workloadForm && this.$refs.workloadForm.resetValidation())
    },

    onEdit(item) {
      if (item.period) {
        this.editedId = null
        this.editedPeriodId = item.period.id
        this.form = {
          ...this.emptyForm(),
          project_id: item.period.project_id,
          period: [item.period.date_from, item.period.date_to],
          hours: Math.floor(item.period.minutes / 60),
          minutes: item.period.minutes % 60,
          description: item.period.description,
        }
      } else {
        this.editedId = item.id
        this.editedPeriodId = null
        this.form = {
          ...this.emptyForm(),
          project_id: item.project_id,
          period: [item.date, item.date],
          hours: Math.floor(item.minutes / 60),
          minutes: item.minutes % 60,
          description: item.description,
        }
      }
      this.formDialog = true
      this.$nextTick(() => this.$refs.workloadForm && this.$refs.workloadForm.resetValidation())
    },

    closeForm() {
      this.formDialog = false
      this.editedId = null
      this.editedPeriodId = null
      this.form = this.emptyForm()
    },

    async save() {
      if (!this.$refs.workloadForm.validate()) return

      this.saving = true
      try {
        const payload = {
          project_id: this.form.project_id,
          hours: this.form.hours,
          minutes: this.form.minutes,
          description: this.form.description.trim(),
        }

        let message = 'Занятость сохранена'

        if (this.isPeriodMode) {
          payload.date_from = this.form.period[0]
          payload.date_to = this.form.period[1]

          if (this.editedPeriodId) {
            await this.updateWorkloadPeriod({ id: this.editedPeriodId, data: payload })
          } else {
            if (this.editedId) payload.replace_workload_id = this.editedId
            await this.createWorkloadPeriod(payload)
            message = `Период сохранён, дней: ${this.periodDays}`
          }
        } else if (this.editedId) {
          payload.dates = this.expandPeriod(this.form.period)
          await this.updateWorkload({ id: this.editedId, data: payload })

          if (payload.dates.length > 1) {
            message = `Сохранено записей: ${payload.dates.length}`
          }
        } else {
          payload.dates = this.expandPeriod(this.form.period)
          const created = await this.createWorkload(payload)

          if (Array.isArray(created) && created.length > 1) {
            message = `Добавлено записей: ${created.length}`
          }
        }

        this.$store.commit('shared/SET_SUCCESS', { message }, { root: true })
        this.closeForm()
        await this.loadReport()
      } catch (error) {
        await this.showError(error, 'Не удалось сохранить занятость')
        console.error(error)
      } finally {
        this.saving = false
      }
    },

    onDelete(item) {
      this.workloadToDelete = item
      this.deleteDialog = true
    },

    async confirmDelete() {
      if (!this.workloadToDelete?.id) return

      this.deleting = true
      try {
        if (this.workloadToDelete.period) {
          await this.deleteWorkloadPeriod(this.workloadToDelete.period.id)
        } else {
          await this.deleteWorkload(this.workloadToDelete.id)
        }
        this.deleteDialog = false
        this.workloadToDelete = null
        this.$store.commit('shared/SET_SUCCESS', { message: 'Занятость удалена' }, { root: true })
        await this.loadReport()
      } catch (error) {
        await this.showError(error, 'Не удалось удалить занятость')
        console.error(error)
      } finally {
        this.deleting = false
      }
    },
  },
}
</script>

<style scoped>
.document-flow-workloads-fab {
  position: fixed;
  right: 18px;
  bottom: 18px;
  z-index: 4;
}
</style>
